import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from "../../../ui/dialog";
import Button from "../../../button-v1";
import styles from "./styles.module.css";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createMonitoringSession } from "../../../../services/patient/details";
import { getPatientLocationId } from "../../../../utils/helper";
import { createPatientNote } from "../../../../services/patient/settings";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

const AddNoteDialogue = ({
  isOpen,
  onClose,
  note,
  onSave,
  monitoringSeconds,
  showNoteTypeSelector = false,
  onNoteSaved,
  normalizeBots,
}) => {
  const [queryParams] = useSearchParams();
  const CCM = Boolean(parseInt(queryParams.get("CCM")));
  const RPM = Boolean(parseInt(queryParams.get("RPM")));
  const accountId = sessionStorage.getItem("acc_id");
  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const patientId = sessionStorage.getItem("_pt_id");
  const queryClient = useQueryClient();
  const [editedNote, setEditedNote] = useState("");
  const [selectedNoteType, setSelectedNoteType] = useState(
    note?.note_type || (CCM && RPM)
      ? "rpm_note"
      : CCM
      ? "ccm_note"
      : RPM
      ? "rpm_note"
      : "rpm_note"
  );
  const [error, setError] = useState("");

  const tabSelected = sessionStorage.getItem("tab-selected");

  const createMonitoringSessionMutation = useMutation(
    (sessionData) =>
      // Store under the PATIENT's location, not the viewer's current
      // location (SCRUM-157).
      createMonitoringSession(sessionData, patientId, getPatientLocationId()),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["patient-monitoring-sessions"]);
        toast.success("Session added successfully");
        if (onNoteSaved) {
          onNoteSaved();
        }
        onClose();
      },
      onError: (error) => {
        toast.error("Could not add session");
      },
    }
  );

  const createNoteMutation = useMutation(
    (data) => createPatientNote(data, patientId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["patient-notes"]);
        queryClient.invalidateQueries(["patientNotes"]);
      },
    }
  );

  const handleContinueWithoutNote = () => {
    if (
      monitoringSeconds.ccm_seconds <= 0 &&
      monitoringSeconds.rpm_seconds <= 0
    ) {
      if (onNoteSaved) {
        onNoteSaved();
      }
      onClose();
      return;
    }

    let data;
    if (monitoringSeconds.ccm_seconds > 0) {
      data = {
        total_ccm_seconds: monitoringSeconds.ccm_seconds,
      };
    }

    if (monitoringSeconds.rpm_seconds > 0) {
      data = {
        ...data,
        total_rpm_seconds: monitoringSeconds.rpm_seconds,
      };
    }
    normalizeBots();
    sessionStorage.setItem("patient_detail_tab", "details");
    createMonitoringSessionMutation.mutate(data);

    // Remove mte (manual time entry) flag which checks if threshold valid or invalid
    sessionStorage.removeItem("mte");
  };

  const handleSave = () => {
    sessionStorage.setItem("patient_detail_tab", "details");
    normalizeBots();

    if (!editedNote.trim()) {
      setError("Note cannot be empty");
      return;
    }
    setError("");

    if (
      monitoringSeconds.ccm_seconds <= 0 &&
      monitoringSeconds.rpm_seconds <= 0
    ) {

      createNoteMutation.mutate({
        ...(selectedNoteType === "rpm_note" && {
          note_text: editedNote,
          note_type: "rpm_note",
        }),
        ...(selectedNoteType === "ccm_note" && {
          note_text: editedNote,
          note_type: "ccm_note",
        }),
      });

      if (onNoteSaved) {
        onNoteSaved();
      }
      onClose();
    } else {
      createMonitoringSessionMutation.mutate({
        monitoring_seconds: monitoringSeconds,
        ...(monitoringSeconds.ccm_seconds > 0 && {
          total_ccm_seconds: monitoringSeconds.ccm_seconds,
        }),
        ...(monitoringSeconds.rpm_seconds > 0 && {
          total_rpm_seconds: monitoringSeconds.rpm_seconds,
        }),
      });
      createNoteMutation.mutate({
        ...(selectedNoteType === "rpm_note" && {
          note_text: editedNote,
          note_type: "rpm_note",
        }),
        ...(selectedNoteType === "ccm_note" && {
          note_text: editedNote,
          note_type: "ccm_note",
        }),
      });
    }

    // Remove mte (manual time entry) flag which checks if threshold valid or invalid
    sessionStorage.removeItem("mte");
  };

  useEffect(() => {
    if (createMonitoringSessionMutation.isSuccess) {
      onClose();
    }
  }, [createMonitoringSessionMutation.isSuccess]);

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Note</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className={styles.content}>
            {showNoteTypeSelector && (
              <div className={styles.noteTypeSelector}>
                {RPM && (
                  <Button
                    variant={
                      selectedNoteType === "rpm_note" ? "primary" : "normal"
                    }
                    onClick={() => setSelectedNoteType("rpm_note")}
                  >
                    RPM Note
                  </Button>
                )}
                {CCM && (
                  <Button
                    variant={
                      selectedNoteType === "ccm_note" ? "primary" : "normal"
                    }
                    onClick={() => setSelectedNoteType("ccm_note")}
                  >
                    CCM Note
                  </Button>
                )}
              </div>
            )}
            <textarea
              value={editedNote}
              onChange={(e) => {
                setEditedNote(e.target.value);
                if (e.target.value.trim()) {
                  setError("");
                }
              }}
              className={styles.textarea}
              placeholder="Enter your note here..."
              autoFocus
            />
          </div>
          {error && <div className={styles.error}>{error}</div>}
        </DialogBody>
        <DialogFooter>
          <Button variant="normal" size="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="normal"
            size="default"
            onClick={() => handleContinueWithoutNote()}
          >
            Continue without note
          </Button>
          <Button
            loading={createMonitoringSessionMutation.isLoading}
            variant="primary"
            size="default"
            onClick={() => handleSave()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddNoteDialogue;
